import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Paper,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
} from "@material-ui/core";
import { Edit, Delete, Add, Code } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import Tooltip from "@material-ui/core/Tooltip";
import PageHelpGuide from "../../components/PageHelpGuide";
import { WEB_WIDGET_HELP_DATA } from "../../constants/pageHelp/webWidgetHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import WebWidgetModal from "../../components/WebWidgetModal";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const WebWidget = () => {
  const classes = useStyles();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    fetchWidgets();
  }, []);

  const fetchWidgets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/web-widget");
      setWidgets(data);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setSelectedWidget(null);
    setModalOpen(true);
  };

  const handleEditWidget = (widget) => {
    setSelectedWidget(widget);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedWidget(null);
    fetchWidgets();
  };

  const handleDeleteWidget = async (widgetId) => {
    try {
      await api.delete(`/web-widget/${widgetId}`);
      toast.success("Widget eliminado con éxito");
      fetchWidgets();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCopyCode = (uuid) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
    const script = `<script src="${backendUrl}/webchat.js" data-uuid="${uuid}"></script>`;
    navigator.clipboard.writeText(script);
    toast.success("Código copiado al portapapeles");
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Eliminar Widget"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          handleDeleteWidget(selectedWidget.id);
          setConfirmModalOpen(false);
        }}
      >
        ¿Estás seguro de que deseas eliminar este widget?
      </ConfirmationModal>
      
      <WebWidgetModal
        open={modalOpen}
        onClose={handleCloseModal}
        widgetId={selectedWidget?.id}
      />

      <Paper className={classes.mainPaper} variant="outlined">
        <MainHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <PageHelpGuide
              helpData={WEB_WIDGET_HELP_DATA}
              tooltipText="Guía y Ayuda de Widget Web (Haz clic para ver)"
            />
            <Title>{i18n.t("webWidgets.title")}</Title>
          </div>
          <MainHeaderButtonsWrapper>
            <Button
              variant="contained"
              className={classes.applyButton}
              onClick={handleOpenModal}
            >
              {i18n.t("webWidgets.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        
        <TableContainer className={classes.scrollArea}>
          <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("webWidgets.columns.name")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("webWidgets.columns.connection")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("webWidgets.columns.flowIA")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("webWidgets.columns.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {widgets.map((widget) => (
              <TableRow key={widget.id}>
                <TableCell align="center">{widget.name}</TableCell>
                <TableCell align="center">{widget.whatsapp?.name || "Sin conexión"}</TableCell>
                <TableCell align="center">{widget.flowBuilder?.name || "Sin flujo"}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Copiar código de integración">
                    <IconButton
                      size="small"
                      onClick={() => handleCopyCode(widget.uuid)}
                    >
                      <Code />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar Widget">
                    <IconButton
                      size="small"
                      onClick={() => handleEditWidget(widget)}
                    >
                      <Edit style={{ color: "#00a8ff" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar Widget">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedWidget(widget);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutlineIcon style={{ color: "#e84118" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default WebWidget;
